import React, { useEffect, useRef, useState } from 'react';

// Vertical step-progression wait screen for report generation.
// The backend job lifecycle emits real stage statuses (see ipmatModels.js
// ACTIVE_JOB_STATUSES); we group them into six student-facing steps and
// never surface raw enums or percent numbers — the checked ladder IS the
// progress. Desktop shows full labels; mobile swaps to short ones via CSS.
const STEPS = [
  {
    key: 'context',
    label: 'Fetching your test data',
    short: 'Fetching test data',
    statuses: ['QUEUED', 'BUILDING_CONTEXT'],
  },
  {
    key: 'stats',
    label: 'Analysing sections & topics',
    short: 'Analysing sections',
    statuses: ['GENERATING_STATS'],
  },
  {
    key: 'evidence',
    label: 'Building evidence tables',
    short: 'Evidence tables',
    statuses: ['BUILDING_EVIDENCE'],
  },
  {
    key: 'prompt',
    label: 'Preparing your report brief',
    short: 'Report brief',
    statuses: ['RENDERING_PROMPT'],
  },
  {
    key: 'ai',
    label: 'Writing your insights',
    short: 'Writing your insights',
    statuses: ['CALLING_AI', 'RETRYING'],
  },
  {
    key: 'save',
    label: 'Quality checks & saving',
    short: 'Quality checks & saving',
    statuses: ['POST_PROCESSING', 'SAVING'],
  },
];

// Round to friendly units — honest vagueness beats fake precision.
function formatWaitTime(seconds) {
  if (!seconds || seconds <= 0) return 'a moment';
  if (seconds < 60) {
    const secs = Math.max(5, Math.ceil(seconds / 5) * 5);
    return `about ${secs} seconds`;
  }
  const mins = Math.ceil(seconds / 60);
  return `about ${mins} minute${mins > 1 ? 's' : ''}`;
}

const STALL_MS = 120000; // no stage advance for 2 min → reassure, don't sit silent

export default function GenerationStepper({ status, queueInfo, polling }) {
  const activeIdx = Math.max(
    0,
    STEPS.findIndex((s) => s.statuses.includes(status))
  );
  const activeStatus = status || 'QUEUED';
  const eta = queueInfo?.estimatedWaitSeconds;

  // Stall guard: if the polled stage hasn't advanced for STALL_MS, swap the
  // hint for a reassurance line instead of leaving the user in silence.
  const [stalled, setStalled] = useState(false);
  const lastChangeRef = useRef(Date.now());
  const prevStatusRef = useRef(status);
  useEffect(() => {
    if (status !== prevStatusRef.current) {
      prevStatusRef.current = status;
      lastChangeRef.current = Date.now();
      setStalled(false);
    }
  }, [status]);
  useEffect(() => {
    const t = setInterval(() => {
      setStalled(Date.now() - lastChangeRef.current > STALL_MS);
    }, 15000);
    return () => clearInterval(t);
  }, []);

  let hint = '';
  if (activeStatus === 'QUEUED' || activeStatus === 'BUILDING_CONTEXT') {
    hint = eta && eta > 0
      ? `A few reports are ahead of yours — should be ready in ${formatWaitTime(eta)}.`
      : 'Getting your test data ready...';
  } else if (activeStatus === 'CALLING_AI' || activeStatus === 'RETRYING') {
    hint = stalled
      ? 'Still working — large test histories can take a little longer.'
      : 'The AI is writing your personalised report — this is the longest step (about half a minute).';
  } else if (stalled) {
    hint = 'Still working — large test histories can take a little longer.';
  }

  let footer = '';
  if (eta && eta >= 180) {
    footer = `Your report is being generated in the background. You can close this page and check back in ${formatWaitTime(eta)}.`;
  } else if (polling) {
    footer = "You'll be redirected automatically when ready.";
  }

  return (
    <div className="sg-stepper-wrap">
      <div className="sg-stepper" role="status" aria-live="polite">
        <h2 className="sg-stepper__title">Preparing your IPMAT insights</h2>
        <ol className="sg-stepper__list">
          {STEPS.map((step, i) => {
            const state = i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'pending';
            return (
              <li key={step.key} className={`sg-step sg-step--${state}`}>
                <span className="sg-step__icon" aria-hidden="true">
                  {state === 'done' ? '✓' : state === 'active' ? <span className="sg-step__pulse" /> : null}
                </span>
                <div className="sg-step__body">
                  <span className="sg-step__label">
                    <span className="sg-step__label-full">{step.label}</span>
                    <span className="sg-step__label-short">{step.short}</span>
                  </span>
                  {state === 'active' && hint && <p className="sg-step__hint">{hint}</p>}
                </div>
              </li>
            );
          })}
        </ol>
        {footer && <p className="sg-stepper__footer">{footer}</p>}
      </div>
    </div>
  );
}
